// Client-side cohort scoring for the Compare page's "upload two sets" mode. Lifted from the Cohort
// Diagnostic pipeline: parse a .zip of transcripts, score each on ONE chosen scorecard (import → run),
// and average the per-skill observed scores into a CompareSide so it renders in the same comparison
// table. Transcripts are scored in the browser session and never persisted server-side.

import { unzip, strFromU8 } from 'fflate';
import { isLikelyText } from './importer.js';
import * as api from './api.js';
import type { CompareSide } from './api.js';
import type { ManagerScorecard, AggregateResult } from './types.js';
import { SKILL_ORDER } from './skills.js';

export const MAX_PER_SET = 20; // scoring is sequential (~1.5 min/call), so this is a large-but-usable cap
export const MIN_PER_SET = 2; // one call isn't a cohort

export interface ParsedZip {
  transcripts: { name: string; text: string }[];
  skippedNonText: number;
  skippedBinary: number;
}

/** Parse a .zip entirely in the browser (fflate) into transcript {name,text} entries, skipping
 *  directories, macOS cruft, non-text extensions, and binary-looking content. */
export function parseZip(file: File): Promise<ParsedZip> {
  return file.arrayBuffer().then(
    (ab) =>
      new Promise<ParsedZip>((resolve, reject) => {
        unzip(new Uint8Array(ab), (err, data) => {
          if (err) {
            reject(new Error('Could not read that .zip. Make sure it is a valid, standard zip file.'));
            return;
          }
          const transcripts: { name: string; text: string }[] = [];
          let skippedNonText = 0;
          let skippedBinary = 0;
          for (const [path, bytes] of Object.entries(data)) {
            if (path.endsWith('/')) continue; // directory entry
            if (path.includes('__MACOSX')) continue; // macOS resource fork
            const base = path.split('/').pop() ?? path;
            if (base.startsWith('.')) continue; // hidden / resource file
            if (!isLikelyText(base)) { skippedNonText += 1; continue; }
            const text = strFromU8(bytes);
            const control = (text.slice(0, 2000).match(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g) || []).length;
            if (control > 8 || text.trim().length === 0) { skippedBinary += 1; continue; }
            transcripts.push({ name: base, text });
          }
          resolve({ transcripts, skippedNonText, skippedBinary });
        });
      }),
  );
}

/** Score a set of transcripts on one scorecard, sequentially. Reuses the per-skill cache (fresh=false).
 *  Reports progress after each call. Fails fast if the first couple of calls error with nothing scored
 *  (a service/auth problem, not the data). A per-transcript error is tolerated once scoring has begun. */
export async function scoreSet(
  transcripts: { name: string; text: string }[],
  scorecard: ManagerScorecard,
  signal: AbortSignal,
  onProgress: (done: number, total: number) => void,
): Promise<AggregateResult[]> {
  const out: AggregateResult[] = [];
  let done = 0;
  let anySuccess = false;
  let consecutiveFail = 0;
  for (const t of transcripts) {
    if (signal.aborted) break;
    try {
      const imp = await api.importTranscript(t.text, signal);
      let agg: AggregateResult | null = null;
      for await (const ev of api.runScoring(imp.session_id, t.text, scorecard, signal, false)) {
        if (ev.t === 'aggregate_ready') agg = ev.result;
        else if (ev.t === 'run_error') throw new Error(ev.message);
      }
      if (agg) { out.push(agg); anySuccess = true; consecutiveFail = 0; }
      else consecutiveFail += 1;
    } catch (e) {
      if (signal.aborted) break;
      consecutiveFail += 1;
      if (!anySuccess && consecutiveFail >= 2) {
        throw new Error('Scoring is failing — check your connection or sign-in, then try again.');
      }
    }
    done += 1;
    onProgress(done, transcripts.length);
  }
  return out;
}

/** Score a set of transcripts, AUTO-CLASSIFYING each: every transcript is scored on the call-type template
 *  the import step classifies for it (exactly like a single import), rather than one fixed scorecard. Used
 *  by the Compare ZIP columns. Same fail-fast + progress contract as scoreSet. A transcript the classifier
 *  can't template (imp.template null) is skipped. */
export async function scoreSetAuto(
  transcripts: { name: string; text: string }[],
  signal: AbortSignal,
  onProgress: (done: number, total: number) => void,
): Promise<AggregateResult[]> {
  const out: AggregateResult[] = [];
  let done = 0;
  let anySuccess = false;
  let consecutiveFail = 0;
  for (const t of transcripts) {
    if (signal.aborted) break;
    try {
      const imp = await api.importTranscript(t.text, signal);
      if (!imp.template) {
        consecutiveFail += 1;
      } else {
        let agg: AggregateResult | null = null;
        for await (const ev of api.runScoring(imp.session_id, t.text, imp.template, signal, false)) {
          if (ev.t === 'aggregate_ready') agg = ev.result;
          else if (ev.t === 'run_error') throw new Error(ev.message);
        }
        if (agg) { out.push(agg); anySuccess = true; consecutiveFail = 0; }
        else consecutiveFail += 1;
      }
    } catch (e) {
      if (signal.aborted) break;
      consecutiveFail += 1;
      if (!anySuccess && consecutiveFail >= 2) {
        throw new Error('Scoring is failing — check your connection or sign-in, then try again.');
      }
    }
    done += 1;
    onProgress(done, transcripts.length);
  }
  return out;
}

/** Average a set's scored calls into a CompareSide (per-skill mean of observed scores, in canonical
 *  skill order; final = mean of final_score; count = calls that scored). */
export function averageSide(id: string, name: string, aggregates: AggregateResult[]): CompareSide {
  const byId = new Map<string, { sum: number; n: number }>();
  let finalSum = 0;
  let finalN = 0;
  for (const agg of aggregates) {
    if (typeof agg.final_score === 'number') { finalSum += agg.final_score; finalN += 1; }
    for (const row of agg.skill_breakdown) {
      if (row.actual_score == null) continue;
      const e = byId.get(row.skill_id) ?? { sum: 0, n: 0 };
      e.sum += row.actual_score;
      e.n += 1;
      byId.set(row.skill_id, e);
    }
  }
  return {
    id,
    name,
    count: aggregates.length,
    final: finalN ? finalSum / finalN : null,
    scores: SKILL_ORDER.map((s) => {
      const e = byId.get(s.id);
      return { key: s.id, label: s.label, avg: e && e.n ? e.sum / e.n : null };
    }),
  };
}
